import { API_PATHS, ROLE_LABELS, type AuthSession, type User } from '@uroute/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  ApiError,
  SessionExpiredError,
  api,
  isNetworkError,
  setSessionExpiredHandler,
  tokenStore,
} from './api';

interface AuthState {
  user: User | null;
  loading: boolean;
  /** Friendly, user-facing message (session expiry, sign-out reason). */
  notice: string | null;
  login: (email: string, password: string, stay: boolean) => Promise<User>;
  logout: (notice?: string) => Promise<void>;
  reload: () => Promise<void>;
  clearNotice: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

const USER_CACHE_KEY = 'uroute.driver.user';

function readCachedUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function friendlyLoginError(err: unknown): Error {
  if (err instanceof ApiError) {
    if (err.status === 401) return new Error('Email or password is incorrect.');
    if (err.status === 429)
      return new Error('Too many sign-in attempts. Wait a minute and try again.');
    if (err.status >= 500) return new Error('UR-ROUTE is having trouble right now. Try again shortly.');
    return new Error(err.message);
  }
  if (isNetworkError(err))
    return new Error("Can't reach UR-ROUTE. Check your connection and try again.");
  return err instanceof Error ? err : new Error('Sign-in failed.');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(Boolean(tokenStore.access()));
  const [notice, setNotice] = useState<string | null>(null);

  const signOutLocal = useCallback((message?: string) => {
    tokenStore.clear();
    try {
      localStorage.removeItem(USER_CACHE_KEY);
    } catch {
      /* ignore */
    }
    setUser(null);
    if (message) setNotice(message);
    if (typeof caches !== 'undefined') {
      void caches.delete('api-reads').catch(() => undefined);
    }
  }, []);

  /* Session expiry raised by the API layer (refresh rejected). */
  useEffect(() => {
    setSessionExpiredHandler(() => signOutLocal('Your session has expired. Please sign in again.'));
    return () => setSessionExpiredHandler(null);
  }, [signOutLocal]);

  const reload = useCallback(async () => {
    if (!tokenStore.access()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await api.get<User>(API_PATHS.auth.me);
      try {
        localStorage.setItem(USER_CACHE_KEY, JSON.stringify(me));
      } catch {
        /* ignore */
      }
      setUser(me);
    } catch (err) {
      if (err instanceof SessionExpiredError) {
        signOutLocal('Your session has expired. Please sign in again.');
      } else if (err instanceof ApiError && err.status === 401) {
        signOutLocal();
      } else if (isNetworkError(err)) {
        // Offline with a stored token: restore the cached user so the app
        // shell renders instead of bouncing to sign-in; the refresh will
        // sort itself out when connectivity returns.
        const cached = readCachedUser();
        if (cached) setUser(cached);
      } else {
        signOutLocal();
      }
    } finally {
      setLoading(false);
    }
  }, [signOutLocal]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /* Sign-out in another tab of this browser. */
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === 'uroute.driver.accessToken') {
        if (!tokenStore.access() && user) signOutLocal('Signed out in another tab.');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user, signOutLocal]);

  const login = useCallback(
    async (email: string, password: string, stay: boolean): Promise<User> => {
      let session: AuthSession;
      try {
        session = await api.post<AuthSession>(API_PATHS.auth.login, { email, password });
      } catch (err) {
        throw friendlyLoginError(err);
      }
      tokenStore.set(
        {
          access: session.accessToken,
          refresh: session.refreshToken,
          expiresAt: Date.now() + session.expiresIn * 1000,
        },
        stay,
      );
      setNotice(null);
      try {
        localStorage.setItem(USER_CACHE_KEY, JSON.stringify(session.user));
      } catch {
        /* ignore */
      }
      setUser(session.user);
      return session.user;
    },
    [],
  );

  const logout = useCallback(
    async (message?: string) => {
      const refreshToken = tokenStore.refresh();
      const accessToken = tokenStore.access();
      /* Local sign-out first so the UI never waits on the network. */
      signOutLocal(message);
      if (refreshToken && accessToken) {
        void fetch(API_PATHS.auth.logout, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({ refreshToken }),
        }).catch(() => undefined);
      }
    },
    [signOutLocal],
  );

  const value = useMemo<AuthState>(
    () => ({ user, loading, notice, login, logout, reload, clearNotice: () => setNotice(null) }),
    [user, loading, notice, login, logout, reload],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Route guard: signed-in driver only. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="pwa-splash" data-testid="app-splash">
        <span className="u-wordmark">UR-ROUTE</span>
        <span className="pwa-splash__hint">Starting driver app…</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/signin" replace state={{ from: location.pathname }} />;
  if (user.role !== 'DRIVER') return <DriverOnlyNotice />;
  return <>{children}</>;
}

function DriverOnlyNotice() {
  const { user, logout } = useAuth();
  return (
    <div className="pwa-splash" data-testid="role-gate">
      <span className="u-wordmark">UR-ROUTE</span>
      <p className="pwa-splash__hint" style={{ maxWidth: '30ch', textAlign: 'center' }}>
        This app is for driver accounts. You&rsquo;re signed in as{' '}
        <strong>{user ? ROLE_LABELS[user.role] : 'a non-driver user'}</strong>.
      </p>
      <button
        type="button"
        className="u-btn u-btn--secondary pwa-btn"
        data-testid="role-gate-signout"
        onClick={() => void logout()}
      >
        Sign out
      </button>
    </div>
  );
}
